import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ManageFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.post(`${API}/faqs`, formData, { headers });
      toast.success("FAQ added successfully!");
      setIsDialogOpen(false);
      setFormData({ question: "", answer: "" });
      fetchFAQs();
    } catch (error) {
      console.error("Error adding FAQ:", error);
      toast.error("Failed to add FAQ");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API}/faqs/${id}`, { headers });
      toast.success("FAQ deleted successfully!");
      fetchFAQs();
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ");
    }
  };

  return (
    <div data-testid="manage-faqs">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage FAQs</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-faq-button">
              <Plus size={20} className="mr-2" /> Add FAQ
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="faq-dialog">
            <DialogHeader>
              <DialogTitle>Add FAQ</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="question">Question</Label>
                <Input
                  id="question"
                  name="question"
                  data-testid="faq-question-input"
                  value={formData.question}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="answer">Answer</Label>
                <Textarea
                  id="answer"
                  name="answer"
                  rows={4}
                  data-testid="faq-answer-input"
                  value={formData.answer}
                  onChange={handleChange}
                  required
                />
              </div>
              <Button type="submit" className="w-full" data-testid="faq-submit-button">
                Add FAQ
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <Card key={faq.id} data-testid={`faq-card-${index}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{faq.question}</CardTitle>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(faq.id)}
                  data-testid={`delete-faq-${index}`}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-sm">{faq.answer}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {faqs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No FAQs yet. Click "Add FAQ" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default ManageFAQs;
